import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shareFolder } from '../../api/share.ts';
import { useAuth } from '../../store/auth.ts';
import { toast } from '../../store/toast.ts';
import { Modal } from '../common/Modal.tsx';
import { IconClose, IconPlus } from '../../lib/icons.tsx';
import type { ApiNode } from '../../api/types.ts';

interface Props {
  namespaceId: string;
  node: ApiNode;
  onClose: () => void;
}

interface Invitee { email: string; role: 'editor' | 'viewer' }

export function ShareFolderDialog({ namespaceId, node, onClose }: Props) {
  const queryClient = useQueryClient();
  const bootstrap = useAuth((s) => s.bootstrap);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer'>('editor');
  const [invitees, setInvitees] = useState<Invitee[]>([]);

  const add = (): void => {
    const value = email.trim().toLowerCase();
    if (!value || invitees.some((i) => i.email === value)) return;
    setInvitees([...invitees, { email: value, role }]);
    setEmail('');
  };

  const share = useMutation({
    mutationFn: () => shareFolder(namespaceId, node.path, invitees),
    onSuccess: (result) => {
      toast.success(
        `'${result.name}' 을(를) 공유했습니다. 왼쪽 '공유 폴더' 목록에서 열 수 있습니다.`,
      );
      // 네임스페이스 목록이 바뀌었으니 다시 불러온다
      void bootstrap();
      void queryClient.invalidateQueries({ queryKey: ['nodes', namespaceId] });
      void queryClient.invalidateQueries({ queryKey: ['shared-folders'] });
      onClose();
    },
    onError: (e: Error) => { toast.error(e); },
  });

  return (
    <Modal
      title={`'${node.name}' 폴더 공유`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>취소</button>
          <button
            className="btn btn--primary"
            disabled={share.isPending}
            onClick={() => { share.mutate(); }}
          >
            {share.isPending ? <span className="spinner" /> : '공유하기'}
          </button>
        </>
      }
    >
      <p className="modal__text">
        이 폴더가 <strong>별도 드라이브로 분리</strong>되어 참여자의 사이드바에 나타납니다.
        내 드라이브의 원래 위치에서는 사라지고, 공유를 해제하면 되돌아옵니다.
      </p>

      <div className="field">
        <label htmlFor="invite-email">참여자 이메일</label>
        <div className="row">
          <input
            id="invite-email" className="input" type="email" placeholder="name@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          />
          <select
            className="input" style={{ width: 110 }} value={role}
            onChange={(e) => { setRole(e.target.value as 'editor' | 'viewer'); }}
            aria-label="권한"
          >
            <option value="editor">편집 가능</option>
            <option value="viewer">보기 전용</option>
          </select>
          <button className="btn btn--icon" onClick={add} aria-label="추가">
            <IconPlus size={16} />
          </button>
        </div>
      </div>

      {invitees.map((invitee) => (
        <div key={invitee.email} className="row" style={{ marginBottom: 6 }}>
          <span className="spacer truncate">{invitee.email}</span>
          <span className="frow__badge">
            {invitee.role === 'editor' ? '편집 가능' : '보기 전용'}
          </span>
          <button
            className="btn btn--sm btn--ghost btn--icon"
            aria-label="제외"
            onClick={() => { setInvitees(invitees.filter((i) => i.email !== invitee.email)); }}
          >
            <IconClose size={14} />
          </button>
        </div>
      ))}

      {invitees.length === 0 && (
        <p className="faint" style={{ fontSize: 12 }}>
          참여자를 나중에 추가해도 됩니다. 가입하지 않은 이메일은 초대장으로 남습니다.
        </p>
      )}
    </Modal>
  );
}
